import React, { useState } from "react";
import { toast } from "sonner";
import {
  Download,
  Share2,
  Mail,
  MessageCircle,
  Link2,
  Loader2,
  X,
  ExternalLink,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Compact download + share widget for a transfer receipt.
 *
 * Props:
 *   kind:   "local" | "internal"
 *   refId:  the transfer's ref_id
 *   canDownload:  boolean (default true)
 *   canShare:     boolean (default true)
 */
export default function TransferReceiptActions({ kind, refId, canDownload = true, canShare = true, compact = true }) {
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const base = (api.defaults?.baseURL || "").replace(/\/api$/, "");
      const url = `${base}/api/wallet/transfer-receipt/${kind}/${refId}/pdf?mode=attachment`;
      const token = localStorage.getItem("token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `transfer-${refId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error("Failed to download receipt");
    } finally { setDownloading(false); }
  };

  return (
    <>
      <div className={compact ? "inline-flex items-center gap-1" : "inline-flex items-center gap-2"} data-testid={`receipt-actions-${refId}`}>
        {canDownload && (
          <button
            onClick={download}
            disabled={downloading}
            title="Download PDF receipt"
            data-testid={`receipt-download-${refId}`}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-300 text-slate-700 text-[11px] font-semibold hover:bg-slate-50 disabled:opacity-40"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {compact ? "" : "Download"}
          </button>
        )}
        {canShare && (
          <button
            onClick={() => setShareOpen(true)}
            title="Share receipt"
            data-testid={`receipt-share-${refId}`}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-[#0F62FE] text-[#0F62FE] text-[11px] font-semibold hover:bg-blue-50"
          >
            <Share2 className="w-3.5 h-3.5" />
            {compact ? "" : "Share"}
          </button>
        )}
      </div>

      {shareOpen && (
        <ShareModal kind={kind} refId={refId} onClose={() => setShareOpen(false)} />
      )}
    </>
  );
}

function ShareModal({ kind, refId, onClose }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wallet/transfer-receipt/${kind}/${refId}/share`);
        setShare(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to prepare share link");
        onClose();
      } finally { setLoading(false); }
    })();
  }, [kind, refId, onClose]);

  const base = (api.defaults?.baseURL || "").replace(/\/api$/, "");
  const publicUrl = share?.absolute_public_url || (share ? `${base}${share.public_path}` : "");
  const pdfUrl = share?.absolute_pdf_url || (share ? `${base}${share.pdf_path}` : "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied");
    } catch { toast.error("Copy failed"); }
  };

  const whatsappHref = share
    ? `https://wa.me/?text=${encodeURIComponent(share.whatsapp_text?.replace(share.public_path, publicUrl) || publicUrl)}`
    : "#";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="receipt-share-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-md p-6 animate-in zoom-in-95 duration-200">
        <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Share receipt
        </div>
        <div className="text-xs text-slate-500 mb-4">
          Ref <span className="font-mono">#{(share?.short_ref) || refId.slice(0, 8).toUpperCase()}</span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Preparing share link…
          </div>
        ) : share && (
          <div className="space-y-2">
            <button
              onClick={copy}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-left"
              data-testid="receipt-share-copy"
            >
              <Link2 className="w-5 h-5 text-[#0F62FE]" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Copy public link</div>
                <div className="text-[11px] text-slate-500 truncate font-mono">{publicUrl}</div>
              </div>
            </button>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-slate-200 hover:bg-slate-50"
              data-testid="receipt-share-whatsapp"
            >
              <MessageCircle className="w-5 h-5 text-emerald-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Send via WhatsApp</div>
                <div className="text-[11px] text-slate-500">Opens WhatsApp with a prefilled message</div>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
            </a>
            <button
              onClick={() => setEmailOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-left"
              data-testid="receipt-share-email"
            >
              <Mail className="w-5 h-5 text-rose-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Send by email</div>
                <div className="text-[11px] text-slate-500">Delivers the PDF as attachment (via Resend)</div>
              </div>
            </button>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-slate-200 hover:bg-slate-50"
              data-testid="receipt-share-open-pdf"
            >
              <Download className="w-5 h-5 text-slate-700" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Open PDF in new tab</div>
                <div className="text-[11px] text-slate-500">Public link · no login required</div>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
            </a>
          </div>
        )}

        {emailOpen && (
          <EmailModal kind={kind} refId={refId} onClose={() => setEmailOpen(false)} />
        )}
      </div>
    </div>
  );
}

function EmailModal({ kind, refId, onClose }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSending(true);
    try {
      await api.post(`/wallet/transfer-receipt/${kind}/${refId}/email`, {
        email: email.trim(),
        message: message.trim() || null,
      });
      toast.success(`Receipt sent to ${email}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email delivery failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="receipt-email-modal">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-md p-6">
        <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="text-lg font-semibold text-slate-900 mb-1">Email the receipt</div>
        <div className="text-xs text-slate-500 mb-4">The PDF will be attached automatically.</div>
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="receipt-email-input"
          placeholder="recipient@example.com"
          className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm mb-3"
        />
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Message (optional)</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="receipt-email-message"
          rows={3}
          maxLength={400}
          placeholder="Add a short note…"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm mb-4"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending}
            data-testid="receipt-email-send"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0043A8] disabled:opacity-50"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
